import {
  animateMini,
  spring,
  type AnimationPlaybackControls,
  type DOMKeyframesDefinition,
} from "framer-motion";
import type { SpringConfig } from "@tokens/index";

/**
 * Imperative motion for selection thumbs (`SegmentedControl`, `SlidingPill`).
 *
 * Compositor-only policy (`tokens/motion-react`): the thumb travels as one
 * `transform` string; its size travels as `width`/`height`. Both go through
 * the Web Animations API (`animateMini`), so no script runs per frame and the
 * spring becomes a `linear()` easing the browser evaluates. A thumb is a
 * pill, so size is never faked with `scaleX`: that bends the end caps
 * (charter §6 cuts "liquid distortion"). Where WAAPI is missing (jsdom) the
 * styles are written directly.
 */

export interface ThumbTarget {
  readonly transform?: string;
  readonly width?: string;
  readonly height?: string;
}

export const thumbTransform = (x: number, y = 0): string =>
  `translate(${String(x)}px, ${String(y)}px)`;

export const px = (value: number): string => `${String(value)}px`;

const supportsWaapi = (el: Element): boolean => typeof el.animate === "function";

/** Write the target without motion. Also the reduced-motion path. */
export function placeThumb(el: HTMLElement, target: ThumbTarget): void {
  if (target.transform !== undefined) el.style.transform = target.transform;
  if (target.width !== undefined) el.style.width = target.width;
  if (target.height !== undefined) el.style.height = target.height;
}

/**
 * Spring the thumb to `target` from wherever it is now. A running animation
 * on the same properties is stopped first and its current value kept.
 */
export function springThumb(
  el: HTMLElement,
  target: ThumbTarget,
  config: SpringConfig,
): AnimationPlaybackControls | null {
  if (!supportsWaapi(el)) {
    placeThumb(el, target);
    return null;
  }
  return animateMini(el, target as DOMKeyframesDefinition, {
    type: spring,
    stiffness: config.stiffness,
    damping: config.damping,
    mass: config.mass,
  });
}

/** First appearance: opacity 0 → 1 over `durationSeconds`, on the compositor. */
export function fadeInThumb(el: HTMLElement, durationSeconds: number): void {
  // Inline opacity stays 1 after the animation so a stylesheet default of 0
  // cannot snap the thumb away when WAAPI fill is `none`.
  el.style.opacity = "1";
  if (durationSeconds <= 0 || !supportsWaapi(el)) {
    return;
  }
  animateMini(el, { opacity: [0, 1] }, { duration: durationSeconds, ease: "linear" });
}
